import { setContext, setTag, setUser } from '@/spa/sentry'

type SentryUser = Parameters<typeof setUser>[0]

/**
 * Shape of the enriched user context we want to surface in Sentry.
 * Every field is optional so callers can progressively provide data
 * as they hydrate user information throughout a request/flow.
 */
interface NTDevSentryUserContext {
  userId?: string | null
  internalUserId?: string | null
  email?: string | null
  role?: string | null
  companyId?: string | null
  companyName?: string | null
  sessionId?: string | null
  firstName?: string | null
  lastName?: string | null
  metadata?: Record<string, unknown>
}

const scrubUndefined = <T extends Record<string, unknown>>(value: T): Partial<T> => {
  return Object.entries(value).reduce((acc, [key, val]) => {
    if (val !== undefined && val !== null) {
      acc[key as keyof T] = val as T[keyof T]
    }
    return acc
  }, {} as Partial<T>)
}

/**
 * Apply the unified Nonresident Tax user context to the active Sentry scope.
 * This ensures every captured error/span includes user identity,
 * role, company, and any supplemental metadata we provide.
 */
export function applySentryUserContext(context: NTDevSentryUserContext): void {
  const { userId, internalUserId, role, companyId } = context

  const normalizedUserId = userId ?? internalUserId ?? undefined
  setUser((normalizedUserId ? { id: normalizedUserId } : {}) as SentryUser)

  // High-signal tags surfaced in the Sentry UI for quick filtering.
  if (normalizedUserId) {
    setTag('auth_state', 'authenticated')
    setTag('auth_user_id', normalizedUserId)
  } else {
    setTag('auth_state', 'anonymous')
    setTag('auth_user_id', 'anonymous')
  }

  if (role) {
    setTag('user_role', role)
  }

  if (companyId) {
    setTag('company_id', companyId)
  }

  setContext(
    'ntdev_user',
    scrubUndefined({
      auth_user_id: userId ?? undefined,
      internal_user_id: internalUserId ?? undefined,
      role: role ?? undefined,
      company_id: companyId ?? undefined,
      environment: process.env.VITE_APP_ENVIRONMENT ?? process.env.NODE_ENV ?? undefined,
      timestamp: new Date().toISOString(),
    }) as Record<string, unknown>,
  )
}

/**
 * Clear the active Sentry user context (for example, on logout).
 */
export function clearSentryUserContext(): void {
  setUser(null)
  setTag('auth_state', 'anonymous')
  setTag('auth_user_id', 'anonymous')
  setTag('user_role', 'guest')
  setTag('company_id', 'none')
  setContext('ntdev_user', null)
}
